import { Injectable } from '@angular/core';
import { GroupedSchoolLocation } from '../types';
import { switchMap } from 'rxjs/operators';
import { sortArray } from '@lx/shared/utils-array';
import { Observable, of } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class SchoolLocationFilterService {
  /**
   * Filter the grouped school locations by query. It will filter the locations by country and by school location.
   * This means that if the query is 'fra' we will show this:
   * - United States
   *  - San Francisco - Berkeley
   * - France
   *  - Lyon
   *  - Nice
   * @param groupedSchoolLocations the grouped school locations to filter (if it is null or undefined, it will return an empty array)
   * @param query the query to filter the locations
   */
  filterGroupedSchoolLocationsByQuery(groupedSchoolLocations: Observable<GroupedSchoolLocation[]>, query: string): Observable<GroupedSchoolLocation[]> {
    if (!groupedSchoolLocations) {
      return of([]);
    }

    // Clean the query by trimming and converting to lower case
    const cleanedQuery = query?.trim()?.toLowerCase();

    // If there is no query, return all the results
    if (!cleanedQuery || !cleanedQuery?.length) {
      return groupedSchoolLocations;
    }

    return groupedSchoolLocations.pipe(
      switchMap((groupedSchoolLocations) => {
        // Filter the locations by school location and with them, create a new array with the filtered locations
        const filteredSchoolLocations = groupedSchoolLocations.reduce((acc, { label, value, weight, items }) => {
          const schoolLocations = items.filter((item) => item.label.toLowerCase().includes(cleanedQuery));
          if (schoolLocations.length > 0) {
            acc.push({
              label,
              value,
              weight,
              items: schoolLocations,
            });
          }
          return acc;
        }, [] as GroupedSchoolLocation[]);

        // Filter the locations only by country
        const filteredByCountry = groupedSchoolLocations.filter((group) => group.label.toLowerCase().includes(cleanedQuery));

        // Merge both filtered lists by skipping duplicates
        const filteredResult = [...filteredSchoolLocations];
        filteredByCountry.forEach((schoolLocation) => {
          if (!filteredResult.some((item) => item.label === schoolLocation.label)) {
            filteredResult.push(schoolLocation);
          }
        });

        // Return filtered result, sorted by weight
        return of(sortArray(filteredResult, [{ property: 'weight' }]));
      }),
    );
  }
}
